export const queryKeys = {
  workspaceAccess: {
    all: ['workspace-access'] as const,
    detail: (wsId?: string, userId?: string | null) => ['workspace-access', wsId, userId] as const,
  },
  discovery: {
    count: (wsId: string | undefined) => ['discovery-count', wsId] as const,
  },
  authorityDocuments: {
    list: (wsId: string | null | undefined) => ['authority-documents', wsId] as const,
    view: (wsId: string, documentId: string) => ['authority-document-view', wsId, documentId] as const,
  },
  company: {
    detail: (wsId: string) => ['company', wsId] as const,
    products: (wsId: string) => ['company-products', wsId] as const,
    contentFreshness: (wsId: string) => ['content-freshness', wsId] as const,
  },
  subjects: {
    list: (wsId: string) => ['subjects', wsId] as const,
  },
  competitors: {
    list: (wsId: string) => ['competitors', wsId] as const,
    subjectRosters: (wsId: string) => ['subject-competitors', wsId] as const,
    subjectRoster: (wsId: string, subjectId: string) => ['subject-competitors', wsId, subjectId, 'all'] as const,
    wltTrend: (wsId: string) => ['competitor-wlt-trend', wsId] as const,
    visibilityRanking: (wsId: string) => ['competitor-visibility-ranking', wsId] as const,
    periodSeries: (wsId: string) => ['competitor-period-series', wsId] as const,
    details: (wsId: string) => ['competitor-detail', wsId] as const,
    detail: (wsId: string, id: string) => ['competitor-detail', wsId, id] as const,
    evaluations: (wsId: string, id: string) => ['competitor-evaluations', wsId, id] as const,
    scorecard: (wsId: string, id: string) => ['competitor-scorecard', wsId, id] as const,
    pageChanges: (wsId: string, id: string) => ['competitor-page-changes', wsId, id] as const,
    researchSources: (wsId: string, id: string) => ['competitor-research-sources', wsId, id] as const,
  },
  prompts: {
    all: (wsId: string) => ['prompts', wsId] as const,
    list: <T extends object>(wsId: string, params?: T) => ['prompts', wsId, params ?? {}] as const,
    // Single-page active-prompt picker (journeys). Distinct from `list`, whose cache
    // entry is the Library's useInfiniteQuery envelope ({pages:[...]}) — sharing the
    // key rendered an empty picker.
    picker: (wsId: string) => ['prompts-picker', wsId] as const,
    groups: (wsId: string) => ['prompt-groups', wsId] as const,
    groupHistory: (wsId: string, topicId: string) => ['prompt-group-history', wsId, topicId] as const,
    groupTrend: (wsId: string, topicId: string) => ['prompt-group-trend', wsId, topicId] as const,
    groupSources: (wsId: string, topicId: string) => ['prompt-group-sources', wsId, topicId] as const,
    groupIssueEvidence: (wsId: string, topicId: string) => ['prompt-group-issue-evidence', wsId, topicId] as const,
    trend: (wsId: string, id: string) => ['prompt-trend', wsId, id] as const,
    sources: (wsId: string, id: string) => ['prompt-sources', wsId, id] as const,
    claims: (wsId: string, id: string) => ['prompt-claims', wsId, id] as const,
    issueEvidence: (wsId: string, id: string) => ['prompt-issue-evidence', wsId, id] as const,
    analyze: (wsId: string) => ['prompts-analyze', wsId] as const,
    // Library composition (`GET /prompts/composition`) — shared by the Identity
    // sub-nav counts, the Configuration panel, and the /home library card. The
    // literal used to be re-typed at each call site.
    composition: (wsId: string | undefined) => ['prompts-composition', wsId] as const,
    foundation: (wsId: string | undefined) => ['prompt-foundation', wsId] as const,
    detail: (wsId: string, id: string) => ['workspace-prompt', wsId, id] as const,
    catalog: (wsId: string) => ['prompt-catalog', wsId] as const,
    // Query Fan Out pilot: filters are part of the key, so a filtered read never
    // serves unfiltered groups from cache (the server keys the same way).
    fanout: <T extends object>(wsId: string, params?: T) => ['prompt-fanout', wsId, params ?? {}] as const,
    history: (wsId: string, id: string) => ['prompt-history', wsId, id] as const,
    stats: (wsId: string, id: string) => ['prompt-stats', wsId, id] as const,
    statsHistory: (wsId: string, id: string) => ['prompt-stats-history', wsId, id] as const,
  },
  evaluations: {
    detail: (wsId: string, id: string) => ['evaluation', wsId, id] as const,
    // Optional topic scope (the Library group drill's Runs view). Invalidation
    // callers use the two-part prefix, which matches topic-scoped keys too.
    workspace: (wsId: string, topicId?: string) =>
      topicId ? (['workspace-evaluations', wsId, topicId] as const) : (['workspace-evaluations', wsId] as const),
    byCoverage: (wsId: string, level: string) => ['evaluations-by-coverage', wsId, level] as const,
  },
  runs: {
    list: (wsId: string) => ['runs', wsId] as const,
    all: (wsId: string) => ['all-runs', wsId] as const,
    queueHealth: (wsId: string) => ['queue-health', wsId] as const,
    evaluations: (wsId: string, runId: string) => ['run-evaluations', wsId, runId] as const,
  },
  reports: {
    list: (wsId: string) => ['reports', wsId] as const,
    workspace: (wsId: string) => ['report', wsId] as const,
    detail: (wsId: string, id?: string) => ['report', wsId, id] as const,
    candidates: (wsId: string, id: string) => ['report-candidates', wsId, id] as const,
    issues: (wsId: string, search: string) => ['report-issues', wsId, search] as const,
  },
  coverage: {
    detail: (wsId: string) => ['coverage', wsId] as const,
  },
  mirror: {
    list: (wsId: string | undefined) => ['mirror', wsId] as const,
    detail: (wsId: string | undefined, urlHash: string | undefined) => ['mirror-detail', wsId, urlHash] as const,
    version: (wsId: string | undefined, urlHash: string | undefined, version: number | undefined) =>
      ['mirror-version', wsId, urlHash, version] as const,
    delivery: (wsId: string | undefined) => ['mirror-delivery', wsId] as const,
    interfaces: (wsId: string | undefined) => ['mirror-interfaces', wsId] as const,
    requests: (wsId: string | undefined, days: number) => ['mirror-requests', wsId, days] as const,
    toolCalls: (wsId: string | undefined, days: number) => ['agent-tool-calls', wsId, days] as const,
    byFact: (wsId: string | undefined, factId: string | undefined) => ['mirror-by-fact', wsId, factId] as const,
    llmsCoverage: (wsId: string | undefined) => ['mirror-llms-coverage', wsId] as const,
    llmsTxt: (wsId: string | undefined) => ['mirror-llms-txt', wsId] as const,
  },
  personas: {
    list: (wsId: string) => ['personas', wsId] as const,
    evaluations: (wsId: string, key: string) => ['persona-evaluations', wsId, key] as const,
  },
  products: {
    list: (wsId: string) => ['products', wsId] as const,
    evaluations: (wsId: string, id: string) => ['product-evaluations', wsId, id] as const,
    competitors: (wsId: string, id: string) => ['product-competitors', wsId, id] as const,
  },
  keywords: {
    list: (wsId: string, sortBy?: string, sortDir?: string) => ['keywords', wsId, sortBy, sortDir] as const,
    all: (wsId: string) => ['keywords', wsId, 'all'] as const,
    volume: (wsId: string) => ['keywords', wsId, 'volume', 'desc'] as const,
  },
  contentInventory: {
    crawlStatus: (wsId: string) => ['content-inventory', wsId, 'crawl-status'] as const,
    list: (wsId: string) => ['content-inventory', wsId] as const,
    overview: (wsId: string) => ['content-overview', wsId] as const,
    siteHealth: (wsId: string, subjectId?: string | null, view: 'full' | 'access' = 'full') =>
      view === 'access'
        ? ['content-site-health', wsId, subjectId ?? null, 'access'] as const
        : ['content-site-health', wsId, subjectId ?? null] as const,
    pageDetail: (wsId: string, pageId: string) => ['content-page-detail', wsId, pageId] as const,
    pageHistory: (wsId: string, pageId: string) => ['content-page-history', wsId, pageId] as const,
    renderProbe: (wsId: string, urlHash: string) => ['content-render-probe', wsId, urlHash] as const,
    pagePlaybooks: (wsId: string, urlHash: string) => ['content-page-playbooks', wsId, urlHash] as const,
    coverageMatrix: (wsId: string, subjectId?: string | null) =>
      ['content-coverage-matrix', wsId, subjectId ?? null] as const,
    vocabCoverage: (wsId: string, subjectId?: string | null) =>
      ['content-vocab-coverage', wsId, subjectId ?? null] as const,
    claimCoverage: (wsId: string, subjectId?: string | null) =>
      ['content-claim-coverage', wsId, subjectId ?? null] as const,
    pageClaims: (wsId: string) => ['content-page-claims', wsId] as const,
    coverageCellPages: (wsId: string, topicId: string | null, contentType: string | null, subjectId?: string | null) =>
      ['content-coverage-cell-pages', wsId, topicId ?? null, contentType ?? null, subjectId ?? null] as const,
    topicClaims: (wsId: string, topicId: string, subjectId?: string | null) =>
      ['content-topic-claims', wsId, topicId, subjectId ?? null] as const,
    vocabTermPages: (wsId: string, topicId: string, theme: string | null, subjectId?: string | null) =>
      ['content-vocab-term-pages', wsId, topicId, theme ?? null, subjectId ?? null] as const,
  },
  features: {
    detail: (wsId: string, id: string) => ['feature-detail', wsId, id] as const,
    drill: (wsId: string, name: string) => ['feature-drill', wsId, name] as const,
    sources: (wsId: string, name: string) => ['feature-sources', wsId, name] as const,
    winRates: (wsId: string) => ['feature-win-rates', wsId] as const,
  },
  identity: {
    metrics: (wsId: string) => ['identity-metrics', wsId] as const,
  },
  sources: {
    citationTrends: (wsId: string, subjectId?: string, engine?: string) =>
      ['citation-trends', wsId, subjectId, engine] as const,
    citationShare: (wsId: string, subjectId?: string, engine?: string, category?: string) =>
      ['citation-share', wsId, subjectId, engine, category] as const,
    citationImpact: (wsId: string, runId?: string, subjectId?: string, category?: string) =>
      ['citation-impact', wsId, runId, subjectId, category] as const,
    newCitations: (wsId: string, subjectId?: string) => ['new-citations', wsId, subjectId] as const,
    corpusCoverage: (wsId: string, subjectId?: string) => ['corpus-coverage', wsId, subjectId] as const,
    corpusTerms: (wsId: string) => ['corpus-terms', wsId] as const,
    termGapDiff: (wsId: string, subjectId?: string) => ['term-gap-diff', wsId, subjectId] as const,
    outreachTargets: (wsId: string, subjectId?: string) => ['outreach-targets', wsId, subjectId] as const,
    coCited: (wsId: string, urlHash: string) => ['co-cited', wsId, urlHash] as const,
    promptTerms: (wsId: string, promptId: string) => ['prompt-terms', wsId, promptId] as const,
    citedThemes: (wsId: string) => ['cited-themes', wsId] as const,
    detail: (wsId: string, urlHash: string, runId?: string) => ['source-detail', wsId, urlHash, runId] as const,
    analysis: (wsId: string, urlHash: string) => ['source-analysis', wsId, urlHash] as const,
    claims: (wsId: string, urlHash: string) => ['source-claims', wsId, urlHash] as const,
    changes: (wsId: string, subjectId?: string) => ['source-changes', wsId, subjectId] as const,
    pages: (wsId: string, bucket?: string, runId?: string, subjectId?: string) =>
      ['source-pages', wsId, bucket, runId, subjectId] as const,
    owned: (wsId: string) => ['owned-sources', wsId] as const,
    ownedWithStats: (wsId: string) => ['owned-sources', wsId, 'stats'] as const,
    surfacePresets: (wsId: string) => ['surface-presets', wsId] as const,
    ownedMovers: (wsId: string) => ['owned-movers', wsId] as const,
  },
  periods: {
    all: (wsId: string) => ['periods', wsId] as const,
    list: (wsId: string, limit?: number) => ['periods', wsId, limit] as const,
    detail: (wsId: string, id: string) => ['period', wsId, id] as const,
    metrics: (wsId: string, id: string, subjectId?: string) => ['period-metrics', wsId, id, subjectId] as const,
    measurement: (wsId: string, id: string, subjectId?: string) => ['period-measurement', wsId, id, subjectId] as const,
    trend: (wsId: string, subjectId?: string) => ['period-trend', wsId, subjectId] as const,
    evaluations: (wsId: string, id: string) => ['period-evaluations', wsId, id] as const,
  },
  playbooks: {
    list: <T extends object>(wsId: string, params?: T) => ['playbooks', wsId, params ?? {}] as const,
    detail: (wsId: string, id: string) => ['playbook', wsId, id] as const,
    portfolio: (wsId: string) => ['playbooks', wsId, 'portfolio'] as const,
    roadmap: (wsId: string) => ['playbooks', wsId, 'roadmap'] as const,
    prompts: (wsId: string, id: string) => ['playbook', wsId, id, 'prompts'] as const,
  },
  workspace: {
    detail: (wsId: string) => ['workspace', wsId] as const,
    list: (userId: string) => ['workspaces', userId] as const,
    users: (wsId: string) => ['workspace-users', wsId] as const,
    invitations: (wsId: string) => ['workspace-invitations', wsId] as const,
    entitlements: (wsId: string) => ['workspace', wsId, 'entitlements'] as const,
  },
  accounts: {
    users: (accountId: string) => ['account-users', accountId] as const,
  },
  traffic: {
    /** `GET /workspaces/{ws}/traffic/summary?days=` — the three evidence lanes
     *  (AI referrals / Agent activity / Agent responses) in Overview. */
    summary: (wsId: string, days: number) => ['traffic-summary', wsId, days] as const,
  },
  integrations: {
    list: (wsId: string) => ['integrations', wsId] as const,
    analyticsTraffic: (wsId: string, provider?: string, start?: string, end?: string, days?: number) =>
      ['analytics-traffic', wsId, provider, start, end, days] as const,
    notionPages: (wsId: string) => ['integration-notion-pages', wsId] as const,
    gitbookSpaces: (wsId: string) => ['integration-gitbook-spaces', wsId] as const,
    ga4Properties: (wsId: string) => ['integration-ga4-properties', wsId] as const,
    cloudflareZones: (wsId: string) => ['integration-cloudflare-zones', wsId] as const,
    cloudflareInstallation: (wsId: string) => ['integration-cloudflare-installation', wsId] as const,
    /** `GET /workspaces/{ws}/integrations/cloudflare/bots?days=[&url_hash=]` —
     *  the crawler lane. `urlHash` narrows it to ONE page (the page dossier's
     *  Crawlers tab); null is the whole-zone view Site › Crawlers renders, and
     *  the two must not share a cache entry. */
    cloudflareBots: (wsId: string | undefined, days: number, urlHash: string | null = null) =>
      ['ai-bot-activity', wsId, days, urlHash] as const,
  },
  jobs: {
    status: (jobId: string) => ['job-status', jobId] as const,
  },
  currentUser: {
    detail: (userId: string) => ['currentUser', userId] as const,
  },
  issues: {
    packetRoot: (wsId: string, issueId: string) => ['evidence-packets', wsId, issueId] as const,
    packetHistory: (wsId: string, issueId: string, offset: number) => ['evidence-packets', wsId, issueId, 'history', offset] as const,
    packetEligibility: (wsId: string, issueId: string, evaluationId: string) => ['evidence-packets', wsId, issueId, 'eligibility', evaluationId] as const,
    root: (wsId: string) => ['issues', wsId] as const,
    investigations: (wsId: string) => ['issue-investigations', wsId] as const,
    investigationPage: (wsId: string, offset: number) => ['issue-investigations', wsId, offset] as const,
    list: <T extends object>(wsId: string, params?: T) => ['issues', wsId, params ?? {}] as const,
    detail: (wsId: string, issueId: string) => ['issues', wsId, 'detail', issueId] as const,
    evidenceHistory: (wsId: string, issueId: string) =>
      ['issues', wsId, 'detail', issueId, 'evidence-history'] as const,
  },
  // Evidence tabs on ThemeDetailPage (`workspace-themes` — see `themesKey` in
  // hooks/api/useThemes.ts — is the theme LIST/roster; these are the per-theme
  // drill queries, kept separate so listing and drilling invalidate independently).
  themeEvidence: {
    evaluations: (wsId: string, themeId: string) => ['theme-evaluations', wsId, themeId] as const,
    sources: (wsId: string, themeId: string) => ['theme-sources', wsId, themeId] as const,
  },
  // Optimization cycles (`hooks/api/useOptimization.ts`; GET_AGENT_READY §8, §9.2).
  // One shared root per workspace so a decision can refresh the whole lane with a
  // single prefix invalidate — the queue, the cycle it belongs to and the history
  // all move when a revision is approved.
  optimization: {
    /** Prefix for everything this destination caches. */
    all: (wsId: string | undefined) => ['optimization', wsId] as const,
    cycles: (wsId: string | undefined, limit: number) => ['optimization', wsId, 'cycles', limit] as const,
    cycle: (wsId: string | undefined, cycleId: string | undefined) => ['optimization', wsId, 'cycle', cycleId] as const,
    queue: (wsId: string | undefined) => ['optimization', wsId, 'queue'] as const,
    proposal: (wsId: string | undefined, proposalId: string | undefined) =>
      ['optimization', wsId, 'proposal', proposalId] as const,
    policies: (wsId: string | undefined) => ['optimization', wsId, 'policies'] as const,
  },
  topics: {
    detail: (wsId: string, topicId: string) => ['topic-detail', wsId, topicId] as const,
    opportunities: (wsId: string) => ['topic-opportunities', wsId] as const,
    all: (wsId: string) => ['topics-all', wsId] as const,
  },
  digests: {
    latest: (wsId: string) => ['digest-latest', wsId] as const,
    byPeriod: (wsId: string, periodId: string) => ['digest-period', wsId, periodId] as const,
  },
} as const;
